import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, MessageCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

export function PlatformSettings() {
  const [payInfo, setPayInfo] = useState({ bank_name: "", account_number: "", account_name: "" });
  const [qrisPath, setQrisPath] = useState("");
  const [qrisFile, setQrisFile] = useState(null);
  const [popup, setPopup] = useState({ enabled: false, whatsapp: "", instagram: "", facebook: "", tiktok: "" });
  const [about, setAbout] = useState({ about_title: "", about_text: "" });
  const [savingAbout, setSavingAbout] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [savingPay, setSavingPay] = useState(false);
  const [savingPopup, setSavingPopup] = useState(false);
  const [uploadingQris, setUploadingQris] = useState(false);

  useEffect(() => {
    api.get("/public/platform").then((r) => {
      setPayInfo(r.data.payment_info || { bank_name: "", account_number: "", account_name: "" });
      setQrisPath(r.data.qris_path || "");
      setPopup({ enabled: false, whatsapp: "", instagram: "", facebook: "", tiktok: "", ...(r.data.popup || {}) });
      setAbout({ about_title: r.data.about_title || "", about_text: r.data.about_text || "" });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const savePay = async (e) => {
    e.preventDefault();
    setSavingPay(true);
    try {
      await api.put("/platform/payment-info", payInfo);
      toast.success("Info pembayaran langganan disimpan");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPay(false);
    }
  };

  const uploadQris = async (e) => {
    e.preventDefault();
    if (!qrisFile) return;
    setUploadingQris(true);
    try {
      const fd = new FormData();
      fd.append("qris", qrisFile);
      const r = await api.post("/platform/qris", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQrisPath(r.data.qris_path);
      setQrisFile(null);
      toast.success("QRIS langganan diperbarui");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingQris(false);
    }
  };

  const savePopup = async (e) => {
    e.preventDefault();
    setSavingPopup(true);
    try {
      await api.put("/platform/popup", popup);
      toast.success("Pengaturan popup kontak disimpan");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPopup(false);
    }
  };

  const qrisUrl = qrisPath
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${qrisPath}?auth=${localStorage.getItem("sb_token")}&t=${Date.now()}`
    : null;
  const inputCls = "h-11 rounded-lg border-slate-200 bg-slate-50";

  const saveAbout = async (e) => {
    e.preventDefault();
    setSavingAbout(true);
    try {
      await api.put("/platform/about", about);
      toast.success("Konten Tentang Kami disimpan — tampil di halaman login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingAbout(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-settings">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-slate-800">Pembayaran Langganan (untuk Sekolah Klien)</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">Rekening & QRIS ini ditampilkan ke sekolah yang baru mendaftar atau memperpanjang paket.</p>
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <form onSubmit={savePay} className="space-y-4" data-testid="platform-payinfo-form">
            <fieldset disabled={!loaded} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="plat-bank">Nama Bank / E-Wallet</Label>
                <Input id="plat-bank" data-testid="platform-bank-input" value={payInfo.bank_name} onChange={(e) => setPayInfo({ ...payInfo, bank_name: e.target.value })} placeholder="Contoh: BSI / BCA / DANA" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plat-acc">Nomor Rekening / E-Wallet</Label>
                <Input id="plat-acc" data-testid="platform-account-input" value={payInfo.account_number} onChange={(e) => setPayInfo({ ...payInfo, account_number: e.target.value })} placeholder="Contoh: 7123456789" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plat-name">Atas Nama</Label>
                <Input id="plat-name" data-testid="platform-name-input" value={payInfo.account_name} onChange={(e) => setPayInfo({ ...payInfo, account_name: e.target.value })} placeholder="Nama pemilik rekening" className={inputCls} />
              </div>
              <Button type="submit" disabled={savingPay} data-testid="platform-payinfo-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {savingPay ? "Menyimpan..." : "Simpan Info Pembayaran"}
              </Button>
            </fieldset>
          </form>
          <div>
            <Label>QRIS untuk Pembayaran Langganan</Label>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              {qrisUrl ? (
                <img src={qrisUrl} alt="QRIS langganan" className="h-32 w-32 rounded-lg border border-slate-200 object-cover" data-testid="platform-qris-preview" />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded-lg border border-dashed border-slate-300 text-center text-xs text-slate-400" data-testid="platform-qris-empty">Belum ada QRIS</div>
              )}
              <form onSubmit={uploadQris} className="flex flex-col gap-3">
                <Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setQrisFile(e.target.files?.[0] || null)} data-testid="platform-qris-file" className="max-w-xs" />
                <Button type="submit" disabled={uploadingQris || !qrisFile} data-testid="platform-qris-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                  {uploadingQris ? "Mengunggah..." : "Unggah QRIS"}
                </Button>
              </form>
            </div>
            <p className="mt-2 text-xs text-slate-400">Setelah klien mengirim bukti bayar, Anda mendapat notifikasi WhatsApp otomatis (butuh sesi WA platform terhubung di menu WhatsApp Gateway).</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-slate-800">Popup Kontak WhatsApp & Media Sosial</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">Tombol kontak melayang di pojok kanan bawah halaman login publik — menambah kepercayaan calon klien.</p>
        <form onSubmit={savePopup} className="mt-4 space-y-4" data-testid="platform-popup-form">
          <fieldset disabled={!loaded} className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={popup.enabled} onCheckedChange={(v) => setPopup({ ...popup, enabled: v })} data-testid="platform-popup-enabled" />
              <Label>Tampilkan popup kontak di halaman login</Label>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pop-wa">No WhatsApp</Label>
                <Input id="pop-wa" data-testid="platform-popup-wa" value={popup.whatsapp} onChange={(e) => setPopup({ ...popup, whatsapp: e.target.value })} placeholder="Contoh: 081234567890" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pop-ig">Link Instagram</Label>
                <Input id="pop-ig" data-testid="platform-popup-ig" value={popup.instagram} onChange={(e) => setPopup({ ...popup, instagram: e.target.value })} placeholder="https://instagram.com/..." className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pop-fb">Link Facebook</Label>
                <Input id="pop-fb" data-testid="platform-popup-fb" value={popup.facebook} onChange={(e) => setPopup({ ...popup, facebook: e.target.value })} placeholder="https://facebook.com/..." className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pop-tt">Link TikTok</Label>
                <Input id="pop-tt" data-testid="platform-popup-tt" value={popup.tiktok} onChange={(e) => setPopup({ ...popup, tiktok: e.target.value })} placeholder="https://tiktok.com/@..." className={inputCls} />
              </div>
            </div>
            <Button type="submit" disabled={savingPopup} data-testid="platform-popup-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
              {savingPopup ? "Menyimpan..." : "Simpan Pengaturan Popup"}
            </Button>
          </fieldset>
        </form>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="platform-about-card">
        <h3 className="font-heading text-base font-semibold text-slate-800">Tentang Kami (Halaman Login)</h3>
        <p className="mt-1 text-xs text-slate-500">Teks ini tampil di halaman login untuk menambah kepercayaan calon klien.</p>
        <form onSubmit={saveAbout} className="mt-4 space-y-4" data-testid="platform-about-form">
          <fieldset disabled={!loaded} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="about-title">Judul</Label>
              <Input id="about-title" data-testid="platform-about-title" value={about.about_title} onChange={(e) => setAbout({ ...about, about_title: e.target.value })} placeholder="Contoh: Tentang EduNexa" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="about-text">Keterangan</Label>
              <Textarea id="about-text" data-testid="platform-about-text" value={about.about_text} onChange={(e) => setAbout({ ...about, about_text: e.target.value })} rows={4} placeholder="Tulis profil singkat, visi, atau keunggulan layanan Anda..." />
            </div>
            <Button type="submit" disabled={savingAbout} data-testid="platform-about-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
              {savingAbout ? "Menyimpan..." : "Simpan Tentang Kami"}
            </Button>
          </fieldset>
        </form>
      </div>
    </div>
  );
}
